import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import StakeTable from './GameEngine/StakeTable';
import { Button, FormSelect, FormInput } from '../components/ui';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Calendar, X } from 'lucide-react';

interface DropdownItem {
  id: number;
  username: string;
  email: string;
}

interface GroupedReportItem {
  affiliate_id: number;
  affiliate_name: string;
  manager_name: string;
  total_payout: number;
  pending_payout: number;
  users_count: number;
}

interface ReportResponse {
  data: GroupedReportItem[];
  total: number;
}

type DatePreset = "today" | "yesterday" | "week" | "month" | "custom" | "all";

const formatDate = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD

const getPresetRange = (
  preset: DatePreset
): { start: string | null; end: string | null } => {
  const now = new Date();
  const todayStr = formatDate(now);

  if (preset === "today") {
    return { start: todayStr, end: todayStr };
  }
  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const s = formatDate(y);
    return { start: s, end: s };
  }
  if (preset === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Sunday
    return { start: formatDate(start), end: todayStr };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(start), end: todayStr };
  }
  return { start: null, end: null };
};

const fetchReportAPI = async (filters: { managerId: string; affiliateId: string; startDate: string; endDate: string; dateType: string; page: number; limit: number; search: string }): Promise<ReportResponse> => {
  const token = localStorage.getItem('token');
  let url = '/admin/affiliate-payouts/report';
  const queryParams = [];
  if (filters.managerId) queryParams.push(`managerId=${filters.managerId}`);
  if (filters.affiliateId) queryParams.push(`affiliateId=${filters.affiliateId}`);
  if (filters.startDate) queryParams.push(`startDate=${filters.startDate}`);
  if (filters.endDate) queryParams.push(`endDate=${filters.endDate}`);
  if (filters.dateType) queryParams.push(`dateType=${filters.dateType}`);
  queryParams.push(`page=${filters.page}`);
  queryParams.push(`limit=${filters.limit}`);
  if (filters.search) queryParams.push(`search=${filters.search}`);

  if (queryParams.length > 0) {
    url += '?' + queryParams.join('&');
  }

  const res = await apiClient.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? { data: res.data.data, total: res.data.total } : { data: [], total: 0 };
};

const fetchManagersAPI = async (): Promise<DropdownItem[]> => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/admin/affiliate-payouts/managers', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const fetchAffiliatesAPI = async (managerId?: string): Promise<DropdownItem[]> => {
  const token = localStorage.getItem('token');
  const url = managerId ? `/admin/affiliate-payouts/affiliates?managerId=${managerId}` : '/admin/affiliate-payouts/affiliates';
  const res = await apiClient.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const AffiliatePayoutReport: React.FC = () => {
  const navigate = useNavigate();
  const role = (localStorage.getItem('role') || '').toLowerCase().trim();
  const isAdmin = role === 'admin';

  // Fetch my profile if affiliate to redirect to details
  const { data: myProfile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await apiClient.get(`/admin/getMineProfile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.data;
    },
    enabled: role === 'affiliate'
  });

  useEffect(() => {
    if (role === 'affiliate' && myProfile?.id) {
      navigate(`/affiliate-payouts/report/details/${myProfile.id}`);
    }
  }, [role, myProfile, navigate]);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(25);

  const [activePreset, setActivePreset] = useState<DatePreset>("all");
  const [dateType, setDateType] = useState('refer');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const [filters, setFilters] = useState({
    managerId: '',
    affiliateId: '',
    startDate: '',
    endDate: '',
    dateType: 'refer',
    page: 1,
    limit: 25,
    search: ''
  });

  const [affiliates, setAffiliates] = useState<DropdownItem[]>([]);

  // Sync state to filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: filterStartDate,
      endDate: filterEndDate,
      dateType,
      page,
      limit,
      search: searchTerm
    }));
  }, [filterStartDate, filterEndDate, dateType, page, limit, searchTerm]);

  // Fetch managers list
  const { data: managers = [] } = useQuery({ queryKey: ['payoutReportManagers'], queryFn: fetchManagersAPI, enabled: isAdmin });

  // Fetch report items (grouped report from backend)
  const { data = { data: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['payoutReport', filters],
    queryFn: () => fetchReportAPI(filters)
  });

  const reportItems = data.data;
  const total = data.total;

  // Fetch affiliates when manager is selected
  useEffect(() => {
    if (isAdmin) {
      fetchAffiliatesAPI(filters.managerId).then(data => setAffiliates(data));
    }
  }, [filters.managerId]);

  // For manager role, fetch their affiliates automatically on load
  useEffect(() => {
    if (role === 'manager') {
      fetchAffiliatesAPI().then(data => setAffiliates(data));
    }
  }, [role]);

  const handlePresetChange = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      return;
    }
    const { start, end } = getPresetRange(preset);
    setFilterStartDate(start || '');
    setFilterEndDate(end || '');
    setPage(1);
  };

  const handleClearFilter = () => {
    setActivePreset("all");
    setFilterStartDate("");
    setFilterEndDate("");
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = '/admin/affiliate-payouts/report';
      const queryParams = [];
      if (filters.managerId) queryParams.push(`managerId=${filters.managerId}`);
      if (filters.affiliateId) queryParams.push(`affiliateId=${filters.affiliateId}`);
      if (filters.startDate) queryParams.push(`startDate=${filters.startDate}`);
      if (filters.endDate) queryParams.push(`endDate=${filters.endDate}`);
      if (filters.dateType) queryParams.push(`dateType=${filters.dateType}`);
      if (filters.search) queryParams.push(`search=${filters.search}`);
      queryParams.push('export=true');
      
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }

      const res = await apiClient.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.data.success || !res.data.data || res.data.data.length === 0) {
        toast.info("No data to export");
        return;
      }
      const dataToExport = res.data.data;

      const headers = ["Affiliate Name", "Manager Name", "Total Payout (USD)", "Pending Payout (USD)", "Users Count"];
      const csvRows = [headers.join(",")];

      for (const item of dataToExport) {
        const row = [
          `"${item.affiliate_name.replace(/"/g, '""')}"`,
          `"${item.manager_name.replace(/"/g, '""')}"`,
          item.total_payout.toFixed(2),
          item.pending_payout.toFixed(2),
          item.users_count
        ];
        csvRows.push(row.join(","));
      }

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const urlBlob = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", urlBlob);
      link.setAttribute("download", `affiliate_payout_report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    { key: 'affiliate_name', label: 'Affiliate' },
    { key: 'manager_name', label: 'Manager' },
    { key: 'total_payout', label: 'Total Payout', render: (val: number) => `$${val.toFixed(2)}` },
    { key: 'pending_payout', label: 'Pending Payout', render: (val: number) => `$${val.toFixed(2)}` },
    { key: 'users_count', label: 'Users Count' },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'right' as const,
      render: (_: any, item: GroupedReportItem) => (
        <Button 
          variant="primary" 
          size="xs" 
          onClick={() => navigate(`/affiliate-payouts/report/details/${item.affiliate_id}?startDate=${filterStartDate}&endDate=${filterEndDate}&dateType=${dateType}`)}
        >
          View Players
        </Button>
      )
    }
  ];

  const PRESETS: { label: string; value: DatePreset }[] = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "Custom Range", value: "custom" },
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]">
      <div className="flex flex-col md:flex-row items-stretch md:items-end gap-4 bg-slate-900 border border-slate-800 p-4 rounded-xl mb-4">
        {isAdmin && (
          <div className="w-full md:w-48">
            <FormSelect 
              label="Filter by Manager" 
              options={[{ value: '', label: 'All Managers' }, ...managers.map((m: DropdownItem) => ({ value: m.id, label: m.username }))]} 
              value={filters.managerId}
              onChange={e => {
                setFilters({ ...filters, managerId: e.target.value, affiliateId: '' });
                setPage(1);
              }} 
            />
          </div>
        )}
        {(isAdmin || role === 'manager') && (
          <div className="w-full md:w-48">
            <FormSelect 
              label="Filter by Affiliate" 
              options={[{ value: '', label: 'All Affiliates' }, ...affiliates.map((a: DropdownItem) => ({ value: a.id, label: a.username }))]} 
              value={filters.affiliateId}
              onChange={e => {
                setFilters({ ...filters, affiliateId: e.target.value });
                setPage(1);
              }} 
              disabled={isAdmin && !filters.managerId}
            />
          </div>
        )}
        
        {/* Date Type Selector */}
        <div className="w-full md:w-44">
          <FormSelect
            label="Date Type"
            options={[
              { value: 'refer', label: 'Refer Date' },
              { value: 'deposit', label: 'Deposit Date' },
              { value: 'wager', label: 'Wager Date' }
            ]}
            value={dateType}
            onChange={e => { setDateType(e.target.value); setPage(1); }}
          />
        </div>

        {/* Presets Select Dropdown */}
        <div className="w-full md:w-44">
          <FormSelect
            label="Date Preset"
            options={PRESETS}
            value={activePreset}
            onChange={e => handlePresetChange(e.target.value as DatePreset)}
          />
        </div>

        {activePreset === 'custom' && (
          <>
            <div className="w-full md:w-40">
              <FormInput 
                type="date" 
                label="Start Date" 
                value={filterStartDate}
                onChange={e => { setFilterStartDate(e.target.value); setPage(1); }}
                className="[color-scheme:dark]"
              />
            </div>
            <div className="w-full md:w-40">
              <FormInput 
                type="date" 
                label="End Date" 
                value={filterEndDate}
                onChange={e => { setFilterEndDate(e.target.value); setPage(1); }}
                className="[color-scheme:dark]"
              />
            </div>
          </>
        )}

        {activePreset !== "all" && (
          <button
            onClick={handleClearFilter}
            className="
              flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl text-sm font-medium h-10 mb-0.5
              bg-red-500/10 border border-red-500/30 text-red-400
              hover:bg-red-500/20 hover:text-red-300 transition-all duration-200
            "
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      <StakeTable
        title="Affiliate Payout Report"
        columns={columns}
        data={reportItems}
        isLoading={isLoading}
        searchPlaceholder="Search affiliate..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={false}
        emptyMessage="No payout calculations found for the active rules."
        actions={
          <Button variant="ghost" onClick={handleExportCSV}>Export CSV</Button>
        }
        hasSidebarMargin={false}
      />
    </div>
  );
};

export default AffiliatePayoutReport;
